import { useRef, useState } from "react";
import {
  deleteCasaMapaAdminAgencyCrmAddress,
  deleteCasaMapaAdminAgencyCrmDocument,
  patchCasaMapaAdminAgencyCrmAddress,
  patchCasaMapaAdminAgencyCrmContact,
  patchCasaMapaAdminAgencyCrmPrimaryContact,
  postCasaMapaAdminAgencyCrmAddress,
  postCasaMapaAdminAgencyCrmComment,
  postCasaMapaAdminAgencyCrmContact,
  uploadCasaMapaAdminAgencyCrmDocument,
} from "../../api/clientCasamapaAdminApi";
import { resolveClientPortalAssetUrl } from "../../api/clientUploadApi";
import { formatClientDateTime } from "../../utils/clientPortalFormat";
import "../../styles/clientPortalUploads.css";

const COPY = "realEstate.casaMapa.admin.agencies.profile.crm.workspace";

const PRIMARY_TABS = ["contacts", "comments"];
const SECONDARY_TABS = ["addresses", "documents"];
const CONTACT_TYPES = ["partner", "primary", "billing", "operations", "other"];

function contactLabel(contact) {
  const name = [contact?.firstName, contact?.lastName].filter(Boolean).join(" ").trim();
  return name || contact?.email || contact?.phone || "—";
}

function formatAddressLine(address) {
  return [
    address?.addressLine1,
    address?.addressLine2,
    [address?.city, address?.region].filter(Boolean).join(", "),
    address?.postalCode,
    address?.country,
  ]
    .filter(Boolean)
    .join(", ");
}

function formatFileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function emptyContactForm() {
  return {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    contactType: "partner",
    notes: "",
  };
}

function emptyAddressForm() {
  return {
    label: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    region: "",
    postalCode: "",
    country: "CO",
    isPrimary: false,
    contactId: "",
    notes: "",
  };
}

function ContactsPanel({
  clientViewId,
  agencySlug,
  contacts,
  primaryContactId,
  t,
  onRefresh,
}) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyContactForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const resetForm = () => {
    setForm(emptyContactForm());
    setEditingId(null);
    setShowForm(false);
    setError("");
  };

  const startEdit = (contact) => {
    setEditingId(contact.id);
    setForm({
      firstName: contact.firstName || "",
      lastName: contact.lastName || "",
      email: contact.email || "",
      phone: contact.phone || "",
      contactType: contact.contactType || "partner",
      notes: contact.notes || "",
    });
    setShowForm(true);
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editingId) {
        await patchCasaMapaAdminAgencyCrmContact(clientViewId, agencySlug, editingId, form);
      } else {
        await postCasaMapaAdminAgencyCrmContact(clientViewId, agencySlug, form);
      }
      resetForm();
      await onRefresh();
    } catch (err) {
      setError(err.message || t(`${COPY}.contacts.saveError`));
    } finally {
      setSaving(false);
    }
  };

  const handleSetPrimary = async (contactId) => {
    setSaving(true);
    setError("");
    try {
      await patchCasaMapaAdminAgencyCrmPrimaryContact(clientViewId, agencySlug, contactId);
      await onRefresh();
    } catch (err) {
      setError(err.message || t(`${COPY}.contacts.primaryError`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cm-crm-workspace__section">
      <div className="cm-crm-workspace__section-head">
        <p className="muted">{t(`${COPY}.contacts.subtitle`)}</p>
        {!showForm ? (
          <button
            type="button"
            className="partner-dashboard-btn"
            onClick={() => {
              setShowForm(true);
              setEditingId(null);
              setForm(emptyContactForm());
            }}
          >
            {t(`${COPY}.contacts.add`)}
          </button>
        ) : null}
      </div>

      {showForm ? (
        <form className="cm-crm-workspace__form" onSubmit={handleSubmit}>
          <div className="cm-crm-workspace__form-grid">
            <label>
              <span>{t(`${COPY}.contacts.fields.firstName`)}</span>
              <input
                value={form.firstName}
                onChange={(event) => setForm((prev) => ({ ...prev, firstName: event.target.value }))}
              />
            </label>
            <label>
              <span>{t(`${COPY}.contacts.fields.lastName`)}</span>
              <input
                value={form.lastName}
                onChange={(event) => setForm((prev) => ({ ...prev, lastName: event.target.value }))}
              />
            </label>
            <label>
              <span>{t(`${COPY}.contacts.fields.email`)}</span>
              <input
                type="email"
                value={form.email}
                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
              />
            </label>
            <label>
              <span>{t(`${COPY}.contacts.fields.phone`)}</span>
              <input
                value={form.phone}
                onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
              />
            </label>
            <label>
              <span>{t(`${COPY}.contacts.fields.type`)}</span>
              <select
                value={form.contactType}
                onChange={(event) => setForm((prev) => ({ ...prev, contactType: event.target.value }))}
              >
                {CONTACT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`${COPY}.contacts.types.${type}`, {}, type)}
                  </option>
                ))}
              </select>
            </label>
            <label className="cm-crm-workspace__form-span">
              <span>{t(`${COPY}.contacts.fields.notes`)}</span>
              <textarea
                rows={3}
                value={form.notes}
                onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
              />
            </label>
          </div>
          <div className="cm-crm-workspace__form-actions">
            <button type="button" className="partner-dashboard-btn" onClick={resetForm} disabled={saving}>
              {t(`${COPY}.cancel`)}
            </button>
            <button type="submit" className="partner-dashboard-btn partner-dashboard-btn--primary" disabled={saving}>
              {saving ? t(`${COPY}.saving`) : editingId ? t(`${COPY}.contacts.saveEdit`) : t(`${COPY}.contacts.save`)}
            </button>
          </div>
        </form>
      ) : null}

      {error ? <p className="cm-agency-profile__error">{error}</p> : null}

      {contacts.length ? (
        <ul className="cm-crm-workspace__list">
          {contacts.map((contact) => (
            <li key={contact.id} className="cm-crm-workspace__card">
              <div className="cm-crm-workspace__card-main">
                <div className="cm-crm-workspace__card-head">
                  <strong>{contactLabel(contact)}</strong>
                  {primaryContactId === contact.id ? (
                    <span className="cm-crm-workspace__badge">{t(`${COPY}.contacts.primary`)}</span>
                  ) : null}
                </div>
                <p className="muted cm-crm-workspace__card-meta">
                  {[contact.email, contact.phone].filter(Boolean).join(" · ") || "—"}
                  {contact.contactType ? ` · ${t(`${COPY}.contacts.types.${contact.contactType}`, {}, contact.contactType)}` : ""}
                </p>
                {contact.notes ? <p className="cm-crm-workspace__card-notes">{contact.notes}</p> : null}
              </div>
              <div className="cm-crm-workspace__card-actions">
                {primaryContactId !== contact.id ? (
                  <button
                    type="button"
                    className="cm-agency-profile__inline-link"
                    disabled={saving}
                    onClick={() => handleSetPrimary(contact.id)}
                  >
                    {t(`${COPY}.contacts.setPrimary`)}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="cm-agency-profile__inline-link"
                  disabled={saving}
                  onClick={() => startEdit(contact)}
                >
                  {t(`${COPY}.edit`)}
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">{t(`${COPY}.contacts.empty`)}</p>
      )}
    </div>
  );
}

function AddressesPanel({ clientViewId, agencySlug, addresses, contacts, t, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyAddressForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const resetForm = () => {
    setForm(emptyAddressForm());
    setEditingId(null);
    setShowForm(false);
    setError("");
  };

  const startEdit = (address) => {
    setEditingId(address.id);
    setForm({
      label: address.label || "",
      addressLine1: address.addressLine1 || "",
      addressLine2: address.addressLine2 || "",
      city: address.city || "",
      region: address.region || "",
      postalCode: address.postalCode || "",
      country: address.country || "CO",
      isPrimary: Boolean(address.isPrimary),
      contactId: address.contactId || "",
      notes: address.notes || "",
    });
    setShowForm(true);
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    const payload = {
      ...form,
      contactId: form.contactId || null,
    };
    try {
      if (editingId) {
        await patchCasaMapaAdminAgencyCrmAddress(clientViewId, agencySlug, editingId, payload);
      } else {
        await postCasaMapaAdminAgencyCrmAddress(clientViewId, agencySlug, payload);
      }
      resetForm();
      await onRefresh();
    } catch (err) {
      setError(err.message || t(`${COPY}.addresses.saveError`));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (addressId) => {
    if (!window.confirm(t(`${COPY}.addresses.deleteConfirm`))) return;
    setSaving(true);
    setError("");
    try {
      await deleteCasaMapaAdminAgencyCrmAddress(clientViewId, agencySlug, addressId);
      await onRefresh();
    } catch (err) {
      setError(err.message || t(`${COPY}.addresses.deleteError`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cm-crm-workspace__section">
      <div className="cm-crm-workspace__section-head">
        <p className="muted">{t(`${COPY}.addresses.subtitle`)}</p>
        {!showForm ? (
          <button
            type="button"
            className="partner-dashboard-btn"
            onClick={() => {
              setShowForm(true);
              setEditingId(null);
              setForm(emptyAddressForm());
            }}
          >
            {t(`${COPY}.addresses.add`)}
          </button>
        ) : null}
      </div>

      {showForm ? (
        <form className="cm-crm-workspace__form" onSubmit={handleSubmit}>
          <div className="cm-crm-workspace__form-grid">
            <label>
              <span>{t(`${COPY}.addresses.fields.label`)}</span>
              <input
                value={form.label}
                onChange={(event) => setForm((prev) => ({ ...prev, label: event.target.value }))}
              />
            </label>
            <label>
              <span>{t(`${COPY}.addresses.fields.contact`)}</span>
              <select
                value={form.contactId}
                onChange={(event) => setForm((prev) => ({ ...prev, contactId: event.target.value }))}
              >
                <option value="">{t(`${COPY}.addresses.noContact`)}</option>
                {contacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contactLabel(contact)}
                  </option>
                ))}
              </select>
            </label>
            <label className="cm-crm-workspace__form-span">
              <span>{t(`${COPY}.addresses.fields.line1`)}</span>
              <input
                value={form.addressLine1}
                onChange={(event) => setForm((prev) => ({ ...prev, addressLine1: event.target.value }))}
              />
            </label>
            <label className="cm-crm-workspace__form-span">
              <span>{t(`${COPY}.addresses.fields.line2`)}</span>
              <input
                value={form.addressLine2}
                onChange={(event) => setForm((prev) => ({ ...prev, addressLine2: event.target.value }))}
              />
            </label>
            <label>
              <span>{t(`${COPY}.addresses.fields.city`)}</span>
              <input
                value={form.city}
                onChange={(event) => setForm((prev) => ({ ...prev, city: event.target.value }))}
              />
            </label>
            <label>
              <span>{t(`${COPY}.addresses.fields.region`)}</span>
              <input
                value={form.region}
                onChange={(event) => setForm((prev) => ({ ...prev, region: event.target.value }))}
              />
            </label>
            <label>
              <span>{t(`${COPY}.addresses.fields.postalCode`)}</span>
              <input
                value={form.postalCode}
                onChange={(event) => setForm((prev) => ({ ...prev, postalCode: event.target.value }))}
              />
            </label>
            <label>
              <span>{t(`${COPY}.addresses.fields.country`)}</span>
              <input
                value={form.country}
                onChange={(event) => setForm((prev) => ({ ...prev, country: event.target.value }))}
              />
            </label>
            <label className="cm-crm-workspace__checkbox">
              <input
                type="checkbox"
                checked={form.isPrimary}
                onChange={(event) => setForm((prev) => ({ ...prev, isPrimary: event.target.checked }))}
              />
              <span>{t(`${COPY}.addresses.fields.primary`)}</span>
            </label>
            <label className="cm-crm-workspace__form-span">
              <span>{t(`${COPY}.addresses.fields.notes`)}</span>
              <textarea
                rows={2}
                value={form.notes}
                onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
              />
            </label>
          </div>
          <div className="cm-crm-workspace__form-actions">
            <button type="button" className="partner-dashboard-btn" onClick={resetForm} disabled={saving}>
              {t(`${COPY}.cancel`)}
            </button>
            <button type="submit" className="partner-dashboard-btn partner-dashboard-btn--primary" disabled={saving}>
              {saving ? t(`${COPY}.saving`) : editingId ? t(`${COPY}.addresses.saveEdit`) : t(`${COPY}.addresses.save`)}
            </button>
          </div>
        </form>
      ) : null}

      {error ? <p className="cm-agency-profile__error">{error}</p> : null}

      {addresses.length ? (
        <ul className="cm-crm-workspace__list">
          {addresses.map((address) => (
            <li key={address.id} className="cm-crm-workspace__card">
              <div className="cm-crm-workspace__card-main">
                <div className="cm-crm-workspace__card-head">
                  <strong>{address.label || t(`${COPY}.addresses.unlabeled`)}</strong>
                  {address.isPrimary ? (
                    <span className="cm-crm-workspace__badge">{t(`${COPY}.addresses.primary`)}</span>
                  ) : null}
                </div>
                <p className="cm-crm-workspace__card-meta">{formatAddressLine(address) || "—"}</p>
                {address.contactName ? (
                  <p className="muted cm-crm-workspace__card-meta">
                    {t(`${COPY}.addresses.pointOfContact`)}: {address.contactName}
                  </p>
                ) : null}
                {address.notes ? <p className="cm-crm-workspace__card-notes">{address.notes}</p> : null}
              </div>
              <div className="cm-crm-workspace__card-actions">
                <button
                  type="button"
                  className="cm-agency-profile__inline-link"
                  disabled={saving}
                  onClick={() => startEdit(address)}
                >
                  {t(`${COPY}.edit`)}
                </button>
                <button
                  type="button"
                  className="cm-agency-profile__inline-link cm-crm-workspace__danger-link"
                  disabled={saving}
                  onClick={() => handleDelete(address.id)}
                >
                  {t(`${COPY}.remove`)}
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">{t(`${COPY}.addresses.empty`)}</p>
      )}
    </div>
  );
}

function CommentsPanel({ clientViewId, agencySlug, comments, t, onRefresh }) {
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    const text = body.trim();
    if (!text) return;
    setSaving(true);
    setError("");
    try {
      await postCasaMapaAdminAgencyCrmComment(clientViewId, agencySlug, { body: text });
      setBody("");
      await onRefresh();
    } catch (err) {
      setError(err.message || t(`${COPY}.comments.saveError`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cm-crm-workspace__section">
      <p className="muted">{t(`${COPY}.comments.subtitle`)}</p>
      <form className="cm-crm-workspace__comment-form" onSubmit={handleSubmit}>
        <textarea
          rows={4}
          value={body}
          placeholder={t(`${COPY}.comments.placeholder`)}
          onChange={(event) => setBody(event.target.value)}
        />
        <div className="cm-crm-workspace__form-actions">
          <button
            type="submit"
            className="partner-dashboard-btn partner-dashboard-btn--primary"
            disabled={saving || !body.trim()}
          >
            {saving ? t(`${COPY}.saving`) : t(`${COPY}.comments.post`)}
          </button>
        </div>
      </form>
      {error ? <p className="cm-agency-profile__error">{error}</p> : null}
      {comments.length ? (
        <ol className="cm-crm-workspace__comments">
          {comments.map((comment) => (
            <li key={comment.id} className="cm-crm-workspace__comment">
              <p className="cm-crm-workspace__comment-body">{comment.description}</p>
              <p className="muted cm-crm-workspace__comment-meta">
                {formatClientDateTime(comment.createdAt)}
                {comment.authorName ? ` · ${comment.authorName}` : ""}
              </p>
            </li>
          ))}
        </ol>
      ) : (
        <p className="muted">{t(`${COPY}.comments.empty`)}</p>
      )}
    </div>
  );
}

function DocumentsPanel({ clientViewId, agencySlug, documents, t, onRefresh }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      await uploadCasaMapaAdminAgencyCrmDocument(clientViewId, agencySlug, file);
      await onRefresh();
    } catch (err) {
      setError(err.message || t(`${COPY}.documents.uploadError`));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(t(`${COPY}.documents.deleteConfirm`, { name: doc.fileName }))) return;
    setUploading(true);
    setError("");
    try {
      await deleteCasaMapaAdminAgencyCrmDocument(clientViewId, agencySlug, doc.documentId);
      await onRefresh();
    } catch (err) {
      setError(err.message || t(`${COPY}.documents.deleteError`));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="cm-crm-workspace__section">
      <div className="cm-crm-workspace__section-head">
        <p className="muted">{t(`${COPY}.documents.subtitle`)}</p>
        <button
          type="button"
          className="partner-dashboard-btn"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? t(`${COPY}.documents.uploading`) : t(`${COPY}.documents.upload`)}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        className="visually-hidden"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/jpeg,image/png,image/webp"
        onChange={handleUpload}
      />
      {error ? <p className="cm-agency-profile__error">{error}</p> : null}
      {documents.length ? (
        <ul className="cp-documents-list">
          {documents.map((doc) => (
            <li key={doc.documentId} className="cp-document-item">
              <div>
                <a
                  href={resolveClientPortalAssetUrl(doc.fileUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="cp-document-name"
                >
                  {doc.fileName}
                </a>
                <div className="muted cp-document-meta">
                  {formatClientDateTime(doc.createdAt)}
                  {doc.fileSize ? ` · ${formatFileSize(doc.fileSize)}` : ""}
                  {doc.uploadedByName ? ` · ${doc.uploadedByName}` : ""}
                </div>
              </div>
              <button
                type="button"
                className="cv-button-link cp-settings-danger-link"
                disabled={uploading}
                onClick={() => handleDelete(doc)}
              >
                {t(`${COPY}.remove`)}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">{t(`${COPY}.documents.empty`)}</p>
      )}
    </div>
  );
}

export default function AgencyCrmWorkspace({
  clientViewId,
  agencySlug,
  contacts = [],
  addresses = [],
  comments = [],
  documents = [],
  primaryContactId = null,
  t,
  onRefresh,
}) {
  const [activeTab, setActiveTab] = useState("contacts");
  const [showSecondary, setShowSecondary] = useState(false);

  const visibleTabs = showSecondary
    ? [...PRIMARY_TABS, ...SECONDARY_TABS]
    : PRIMARY_TABS;

  const handleShowSecondary = () => {
    setShowSecondary(true);
    if (!SECONDARY_TABS.includes(activeTab)) {
      setActiveTab("addresses");
    }
  };

  return (
    <div className="cm-crm-workspace">
      <div className="cm-crm-workspace__tabs" role="tablist" aria-label={t(`${COPY}.title`)}>
        {visibleTabs.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            className={`cm-crm-workspace__tab${activeTab === tab ? " cm-crm-workspace__tab--active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {t(`${COPY}.tabs.${tab}`)}
            {tab === "contacts" && contacts.length ? ` (${contacts.length})` : ""}
            {tab === "addresses" && addresses.length ? ` (${addresses.length})` : ""}
            {tab === "comments" && comments.length ? ` (${comments.length})` : ""}
            {tab === "documents" && documents.length ? ` (${documents.length})` : ""}
          </button>
        ))}
        {!showSecondary ? (
          <button
            type="button"
            className="cm-crm-workspace__tab cm-crm-workspace__tab--more"
            onClick={handleShowSecondary}
          >
            {t(`${COPY}.tabs.showMore`, {}, "Addresses & documents")}
          </button>
        ) : (
          <button
            type="button"
            className="cm-crm-workspace__tab cm-crm-workspace__tab--more"
            onClick={() => {
              setShowSecondary(false);
              if (SECONDARY_TABS.includes(activeTab)) setActiveTab("contacts");
            }}
          >
            {t(`${COPY}.tabs.hideMore`, {}, "Hide addresses & documents")}
          </button>
        )}
      </div>

      <div className="cm-crm-workspace__panel" role="tabpanel">
        {activeTab === "contacts" ? (
          <ContactsPanel
            clientViewId={clientViewId}
            agencySlug={agencySlug}
            contacts={contacts}
            primaryContactId={primaryContactId}
            t={t}
            onRefresh={onRefresh}
          />
        ) : null}
        {activeTab === "addresses" ? (
          <AddressesPanel
            clientViewId={clientViewId}
            agencySlug={agencySlug}
            addresses={addresses}
            contacts={contacts}
            t={t}
            onRefresh={onRefresh}
          />
        ) : null}
        {activeTab === "comments" ? (
          <CommentsPanel
            clientViewId={clientViewId}
            agencySlug={agencySlug}
            comments={comments}
            t={t}
            onRefresh={onRefresh}
          />
        ) : null}
        {activeTab === "documents" ? (
          <DocumentsPanel
            clientViewId={clientViewId}
            agencySlug={agencySlug}
            documents={documents}
            t={t}
            onRefresh={onRefresh}
          />
        ) : null}
      </div>
    </div>
  );
}
